import SEO from '../pages/SEO';
import React from "react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom"; // ✅ Use react-router-dom instead of next/link

export default function SportsInjuriesPage() {
  const injuries = [
    "Ankle sprains and instability",
    "ACL and PCL tears",
    "Meniscus injuries",
    "Rotator cuff tears",
    "Tennis and golfer's elbow",
    "Achilles tendon injuries",
    "Hamstring and quadriceps strains",
    "Shoulder dislocations",
  ];

  const approach = [
    {
      title: "Acute Management",
      description: "RICE protocols, bracing, and immediate care to minimize damage and start healing",
    },
    {
      title: "Accurate Diagnosis",
      description: "Advanced imaging including X-rays, ultrasound, and MRI when needed",
    },
    {
      title: "Treatment Planning",
      description: "Conservative management or surgical intervention based on injury severity and goals",
    },
    {
      title: "Rehabilitation",
      description: "Structured physiotherapy programs supervised by our experienced team",
    },
    {
      title: "Return to Play",
      description: "Sport-specific conditioning and gradual progression to full activity",
    },
    {
      title: "Injury Prevention",
      description: "Education and training to reduce risk of re-injury",
    },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <SEO
        title="Sports Injury Surgeon & Treatment in Hyderabad"
        description="Get assessment and treatment for sports injuries in Hyderabad, including ligament, muscle, tendon and joint injuries, with rehabilitation and return-to-sport planning by Dr. Sujit Kumar."
        keywords="sports injury doctor Hyderabad, sports injury surgeon, ACL injury treatment Hyderabad, ligament injury doctor, sports medicine Hyderabad"
        canonical="https://www.drsujitkumar.com/treatments/sports-injuries"
        robots="index, follow"
      />
      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary to-primary/90 text-primary-foreground py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="max-w-2xl">
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl mb-4 font-[family-name:var(--font-playfair)]">
              Sports Injury Management
            </h1>
            <p className="text-lg text-primary-foreground/90 leading-relaxed">
              Get back in the game with expert care and structured return-to-play programs
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 items-start">
            <div className="relative h-[500px] rounded-lg overflow-hidden shadow-xl">
              <img
                src="/sports-injury-treatment-athlete-rehabilitation-pr.jpg"
                alt="Sports injury treatment"
                className="object-cover w-full h-full"
              />
            </div>

            <div>
              <h2 className="text-3xl font-bold mb-6 font-[family-name:var(--font-playfair)]">
                Comprehensive Sports Injury Care
              </h2>
              <div className="prose prose-lg max-w-none space-y-4">
                <p className="text-foreground leading-relaxed">
                  From acute ankle sprains to ligament tears, we offer comprehensive management including RICE
                  protocols, bracing, imaging, arthroscopy when indicated, and structured return-to-play programs
                  supervised by our physiotherapy team.
                </p>
                <p className="text-foreground leading-relaxed">
                  Whether you're a recreational athlete or competing at a high level, we understand the importance of
                  getting you back to your sport safely and effectively. Our approach combines immediate care, accurate
                  diagnosis, and evidence-based treatment to optimize your recovery.
                </p>
              </div>

              <div className="mt-8">
                <h3 className="text-xl font-semibold mb-4">Common Sports Injuries We Treat</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {injuries.map((injury, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-foreground text-sm">{injury}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-16 bg-muted/30">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12 font-[family-name:var(--font-playfair)]">
            Our Treatment Approach
          </h2>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {approach.map((item, index) => (
              <Card key={index}>
                <CardContent className="p-6">
                  <div className="mb-3 flex items-center justify-center h-10 w-10 rounded-full bg-secondary/10 text-primary font-bold">
                    {index + 1}
                  </div>
                  <h3 className="text-lg font-semibold mb-2">{item.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4 font-[family-name:var(--font-playfair)]">
            Injured? Don't Wait to Get Treatment
          </h2>
          <p className="text-lg mb-8 text-muted-foreground max-w-2xl mx-auto">
            Early intervention leads to better outcomes. Book a consultation to get your recovery started.
          </p>
          <Button size="lg">
            <Link to="/book-appointment" className="flex items-center justify-center">
              Book Appointment
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
